/**
 * Console dedup — suppresses consecutive similar messages after 3 repeats.
 *
 * "Similar" = same prefix up to the first variable part (e.g. ID, number).
 * This catches both exact repeats AND pattern repeats like:
 *   "[foo] Bar for ABC123 failed"
 *   "[foo] Bar for DEF456 failed"
 *
 * Install once in App.vue's <script setup> with `installConsoleDedup()`.
 */

const MAX_REPEATS = 3

let lastPrefix = ''
let repeatCount = 0
let installed = false

/**
 * Extract a "signature" from a message by replacing variable parts
 * (UUIDs, hex strings, numbers, LEGO keys like S0001L02) with placeholders.
 */
function getSignature(message: string): string {
  return message
    .replace(/S\d{3,4}L\d{2}/g, 'S####L##')        // LEGO keys
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}/gi, '<uuid>') // UUID prefixes
    .replace(/\b\d{2,}\b/g, '#')                     // Numbers (2+ digits)
}

function dedup(
  original: (...args: unknown[]) => void,
  args: unknown[],
  method: 'error' | 'warn',
): boolean {
  let message: string
  try {
    message = args.map(a =>
      typeof a === 'string' ? a : JSON.stringify(a)
    ).join(' ')
  } catch {
    // Native consoles accept circular objects and BigInts. If a signature
    // cannot be serialized, forward the original arguments without deduping.
    // This message still breaks the previous consecutive repeat streak.
    lastPrefix = ''
    repeatCount = 0
    return false
  }

  const sig = `${method}:${getSignature(message)}`

  if (sig === lastPrefix) {
    repeatCount++
    if (repeatCount === MAX_REPEATS + 1) {
      original(`[suppressed — similar message repeated, showing no more]`)
    }
    return repeatCount > MAX_REPEATS
  }

  lastPrefix = sig
  repeatCount = 1
  return false
}

export function installConsoleDedup(): void {
  // App setup can run again on remount; stacked wrappers share the counter.
  if (installed) return
  installed = true
  const origError = console.error.bind(console)
  const origWarn = console.warn.bind(console)

  console.error = (...args: unknown[]) => {
    if (!dedup(origError, args, 'error')) origError(...args)
  }
  console.warn = (...args: unknown[]) => {
    if (!dedup(origWarn, args, 'warn')) origWarn(...args)
  }
}
